import {
  isPreStartInheritedEpisode,
  splitWagersByOrigin,
  type OriginWager,
} from './episode-origin.ts';

export interface BalancePoint {
  sequence: number;
  timestampUtc: string | null;
  balance: number;
  pnl: number;
}

export interface MonitorMarker {
  sequence: number;
  timestampUtc: string | null;
  kind: 'STOP' | 'SUCCESS' | 'CONFIG';
  strategyId: string;
  label: string;
  amount: number;
  detail?: string;
}

export interface MonitorEvent {
  sequence: number;
  timestampUtc: string | null;
  kind: 'STOP' | 'SUCCESS' | 'NEW_LOW' | 'NEW_PEAK' | 'BELOW_ZERO' | 'RECOVERED' | 'CONFIG';
  label: string;
  detail: string;
  amount: number | null;
}

export interface StrategyMonitorStats {
  strategyId: string;
  label: string;
  episodes: number;
  wins: number;
  stopouts: number;
  wagers: number;
  pnl: number;
  staked: number;
  roi: number | null;
}

export interface BalanceMonitor {
  balance: number;
  pnl: number;
  startingBalance: number;
  peakBalance: number;
  lowestBalance: number;
  currentDrawdown: number;
  maxDrawdown: number;
  maxDeficit: number;
  series: BalancePoint[];
  markers: MonitorMarker[];
  recentEvents: MonitorEvent[];
  strategies: Record<string, StrategyMonitorStats>;
}

export interface StrategyIdentity {
  id: string;
  targetMultiplier: number;
  entryDrought: number;
  stopDrought: number;
}

const PRIORITY_STOP_IDS = new Set(['2x-d3-s10', '2x-d5-s11', '50x-d103-s161']);
const LARGE_WIN_THRESHOLD = 9;
const RECENT_LIMIT = 20;
const MARKER_BUDGET = 36;
export const MAX_MONITOR_SERIES_POINTS = 400;

export function downsampleBalanceSeries(
  series: readonly BalancePoint[],
  maxPoints = MAX_MONITOR_SERIES_POINTS,
): BalancePoint[] {
  if (series.length <= maxPoints) {
    return [...series];
  }
  const kept = new Map<number, BalancePoint>();
  const add = (point: BalancePoint | undefined) => {
    if (point) {
      kept.set(point.sequence, point);
    }
  };
  add(series[0]);
  add(series[series.length - 1]);
  let minPoint = series[0]!;
  let maxPoint = series[0]!;
  for (const point of series) {
    if (point.balance < minPoint.balance) {
      minPoint = point;
    }
    if (point.balance > maxPoint.balance) {
      maxPoint = point;
    }
  }
  add(minPoint);
  add(maxPoint);
  const stride = (series.length - 1) / (maxPoints - 1);
  for (let index = 0; index < maxPoints; index += 1) {
    add(series[Math.round(index * stride)]);
  }
  return [...kept.values()].sort((left, right) => left.sequence - right.sequence);
}

function settled(wager: OriginWager): boolean {
  return (wager.status ?? 'SETTLED') === 'SETTLED';
}

export function formatStrategyLabel(strategy: Pick<StrategyIdentity, 'targetMultiplier' | 'entryDrought' | 'stopDrought'>): string {
  return `${strategy.targetMultiplier}x D${strategy.entryDrought}-S${strategy.stopDrought}`;
}

export function labelFromStrategyId(strategyId: string, identities: readonly StrategyIdentity[] = []): string {
  const known = identities.find((row) => row.id === strategyId);
  if (known) {
    return formatStrategyLabel(known);
  }
  const match = /^(\d+(?:\.\d+)?)x-d(\d+)-s(\d+)$/i.exec(strategyId);
  if (!match) {
    return strategyId;
  }
  return `${match[1]}x D${match[2]}-S${match[3]}`;
}

function possibleWagers(wager: OriginWager, identities: readonly StrategyIdentity[]): number | null {
  const entry = wager.entryDrought ?? identities.find((row) => row.id === wager.strategyId)?.entryDrought;
  const stop = wager.stopDrought ?? identities.find((row) => row.id === wager.strategyId)?.stopDrought;
  if (entry == null || stop == null) {
    return null;
  }
  return stop - entry;
}

function roundToCent(value: number): number {
  return Math.round(value * 100) / 100;
}

interface EpisodeSummary {
  strategyId: string;
  episodeId: string;
  sequence: number;
  timestampUtc: string | null;
  pnl: number;
  last: OriginWager;
  stopout: boolean;
  win: boolean;
}

function summarizeEpisodes(wagers: readonly OriginWager[], identities: readonly StrategyIdentity[]): EpisodeSummary[] {
  const groups = new Map<string, OriginWager[]>();
  for (const wager of wagers) {
    if (!settled(wager) || isPreStartInheritedEpisode(wager.episodeId)) {
      continue;
    }
    const episodeId = String(wager.episodeId ?? `${wager.strategyId}:unknown`);
    const list = groups.get(episodeId) ?? [];
    list.push(wager);
    groups.set(episodeId, list);
  }
  const summaries: EpisodeSummary[] = [];
  for (const [episodeId, rows] of groups) {
    rows.sort((a, b) => (a.sequenceNumber ?? 0) - (b.sequenceNumber ?? 0) || (a.wagerNumber ?? 0) - (b.wagerNumber ?? 0));
    const last = rows[rows.length - 1]!;
    const pnl = roundToCent(rows.reduce((sum, row) => sum + (Number(row.netPnl) || 0), 0));
    const needed = possibleWagers(last, identities);
    const win = last.result === 'win';
    const stopout = last.result === 'loss' && needed !== null && (last.wagerNumber ?? 0) >= needed;
    if (!win && !stopout) {
      continue;
    }
    summaries.push({
      strategyId: last.strategyId,
      episodeId,
      sequence: last.sequenceNumber ?? 0,
      timestampUtc: last.timestampUtc ?? null,
      pnl,
      last,
      stopout,
      win,
    });
  }
  summaries.sort((a, b) => a.sequence - b.sequence);
  return summaries;
}

export function buildBalanceMonitor(input: {
  wagers: readonly OriginWager[];
  startingBalance: number;
  startSequence: number | null;
  startedAtUtc: string | null;
  lastProcessedSequence: number | null;
  lastProcessedTimestamp: string | null;
  identities?: readonly StrategyIdentity[];
  configChanges?: readonly { sequence: number; fromVersion: number; toVersion: number }[];
}): BalanceMonitor {
  const identities = input.identities ?? [];
  const starting = input.startingBalance;
  const split = splitWagersByOrigin(input.wagers);
  const pnl = roundToCent(split.experimentPnl);
  const balance = roundToCent(starting + pnl);
  const startSequence = input.startSequence ?? 0;

  const bySequence = new Map<number, { timestampUtc: string | null; experimentNet: number }>();
  for (const wager of input.wagers) {
    if (!settled(wager) || isPreStartInheritedEpisode(wager.episodeId)) {
      continue;
    }
    const sequence = wager.sequenceNumber;
    if (sequence == null || !Number.isFinite(sequence)) {
      continue;
    }
    const current = bySequence.get(sequence) ?? { timestampUtc: wager.timestampUtc ?? null, experimentNet: 0 };
    current.experimentNet += Number(wager.netPnl) || 0;
    current.timestampUtc = wager.timestampUtc ?? current.timestampUtc;
    bySequence.set(sequence, current);
  }

  const series: BalancePoint[] = [
    {
      sequence: startSequence,
      timestampUtc: input.startedAtUtc,
      balance: starting,
      pnl: 0,
    },
  ];
  let running = 0;
  const sequences = [...bySequence.keys()].sort((a, b) => a - b);
  for (const sequence of sequences) {
    const row = bySequence.get(sequence)!;
    running = roundToCent(running + row.experimentNet);
    const pointBalance = roundToCent(starting + running);
    if (sequence === startSequence) {
      series[0] = {
        sequence,
        timestampUtc: row.timestampUtc ?? input.startedAtUtc,
        balance: pointBalance,
        pnl: running,
      };
      continue;
    }
    series.push({
      sequence,
      timestampUtc: row.timestampUtc,
      balance: pointBalance,
      pnl: running,
    });
  }
  const lastSequence = input.lastProcessedSequence;
  if (lastSequence != null && (series.length === 0 || series[series.length - 1]!.sequence !== lastSequence)) {
    series.push({
      sequence: lastSequence,
      timestampUtc: input.lastProcessedTimestamp,
      balance,
      pnl,
    });
  }

  let runningPeak = starting;
  let peakBalance = starting;
  let lowestBalance = starting;
  let maxDrawdown = 0;
  const pathEvents: MonitorEvent[] = [];
  let previous = starting;
  for (let index = 0; index < series.length; index += 1) {
    const point = series[index]!;
    const wasPeak = peakBalance;
    const wasLow = lowestBalance;
    runningPeak = Math.max(runningPeak, point.balance);
    peakBalance = Math.max(peakBalance, point.balance);
    lowestBalance = Math.min(lowestBalance, point.balance);
    maxDrawdown = Math.max(maxDrawdown, roundToCent(runningPeak - point.balance));
    if (index === 0) {
      previous = point.balance;
      continue;
    }
    if (point.balance > wasPeak + 1e-9) {
      pathEvents.push({
        sequence: point.sequence,
        timestampUtc: point.timestampUtc,
        kind: 'NEW_PEAK',
        label: 'BALANCE',
        detail: 'NEW PEAK',
        amount: point.balance,
      });
    }
    if (point.balance < wasLow - 1e-9) {
      pathEvents.push({
        sequence: point.sequence,
        timestampUtc: point.timestampUtc,
        kind: 'NEW_LOW',
        label: 'BALANCE',
        detail: 'NEW LOW',
        amount: point.balance,
      });
    }
    if (previous >= 0 && point.balance < 0) {
      pathEvents.push({
        sequence: point.sequence,
        timestampUtc: point.timestampUtc,
        kind: 'BELOW_ZERO',
        label: 'BALANCE',
        detail: 'BELOW $0',
        amount: point.balance,
      });
    }
    if (previous < 0 && point.balance >= 0) {
      pathEvents.push({
        sequence: point.sequence,
        timestampUtc: point.timestampUtc,
        kind: 'RECOVERED',
        label: 'BALANCE',
        detail: 'RECOVERED',
        amount: point.balance,
      });
    }
    previous = point.balance;
  }

  const episodes = summarizeEpisodes(input.wagers, identities);
  const strategyStats: Record<string, StrategyMonitorStats> = {};
  for (const identity of identities) {
    const splitRow = splitWagersByOrigin(input.wagers, identity.id);
    strategyStats[identity.id] = {
      strategyId: identity.id,
      label: formatStrategyLabel(identity),
      episodes: 0,
      wins: 0,
      stopouts: 0,
      wagers: splitRow.experimentWagers,
      pnl: roundToCent(splitRow.experimentPnl),
      staked: splitRow.experimentStaked,
      roi: splitRow.experimentStaked > 0 ? splitRow.experimentPnl / splitRow.experimentStaked : null,
    };
  }
  const episodeEvents: MonitorEvent[] = [];
  const allMarkers: MonitorMarker[] = [];
  for (const episode of episodes) {
    const stats = strategyStats[episode.strategyId] ?? {
      strategyId: episode.strategyId,
      label: labelFromStrategyId(episode.strategyId, identities),
      episodes: 0,
      wins: 0,
      stopouts: 0,
      wagers: 0,
      pnl: 0,
      staked: 0,
      roi: null,
    };
    stats.episodes += 1;
    if (episode.win) {
      stats.wins += 1;
    }
    if (episode.stopout) {
      stats.stopouts += 1;
    }
    strategyStats[episode.strategyId] = stats;
    const label = stats.label;
    if (episode.stopout) {
      allMarkers.push({
        sequence: episode.sequence,
        timestampUtc: episode.timestampUtc,
        kind: 'STOP',
        strategyId: episode.strategyId,
        label,
        amount: episode.pnl,
      });
      episodeEvents.push({
        sequence: episode.sequence,
        timestampUtc: episode.timestampUtc,
        kind: 'STOP',
        label,
        detail: 'STOP',
        amount: episode.pnl,
      });
    } else if (episode.win && (episode.pnl >= LARGE_WIN_THRESHOLD || (episode.last.targetMultiplier ?? 0) >= 10)) {
      allMarkers.push({
        sequence: episode.sequence,
        timestampUtc: episode.timestampUtc,
        kind: 'SUCCESS',
        strategyId: episode.strategyId,
        label,
        amount: episode.pnl,
      });
      episodeEvents.push({
        sequence: episode.sequence,
        timestampUtc: episode.timestampUtc,
        kind: 'SUCCESS',
        label,
        detail: 'SUCCESS',
        amount: episode.pnl,
      });
    }
  }

  const configMarkers: MonitorMarker[] = (input.configChanges ?? []).map((change) => ({
    sequence: change.sequence,
    timestampUtc: series.find((point) => point.sequence === change.sequence)?.timestampUtc ?? input.lastProcessedTimestamp,
    kind: 'CONFIG' as const,
    strategyId: 'config',
    label: 'CONFIG',
    amount: 0,
    detail: `Config changed\nv${change.fromVersion} -> v${change.toVersion}\neffective seq ${change.sequence}`,
  }));
  const configEvents: MonitorEvent[] = (input.configChanges ?? []).map((change) => ({
    sequence: change.sequence,
    timestampUtc: series.find((point) => point.sequence === change.sequence)?.timestampUtc ?? input.lastProcessedTimestamp,
    kind: 'CONFIG',
    label: 'CONFIG',
    detail: `v${change.fromVersion} -> v${change.toVersion}`,
    amount: null,
  }));

  const stopMarkers = allMarkers.filter((row) => row.kind === 'STOP');
  const winMarkers = allMarkers.filter((row) => row.kind === 'SUCCESS');
  const chartStops =
    stopMarkers.length > MARKER_BUDGET
      ? stopMarkers.filter((row) => PRIORITY_STOP_IDS.has(row.strategyId))
      : stopMarkers;
  const chartWins = winMarkers.filter((row) => row.strategyId.startsWith('50x'));
  const markers = [...chartStops, ...chartWins, ...configMarkers].sort((a, b) => a.sequence - b.sequence);

  const recentEvents = [...episodeEvents, ...pathEvents, ...configEvents]
    .sort((a, b) => b.sequence - a.sequence || eventRank(a.kind) - eventRank(b.kind))
    .slice(0, RECENT_LIMIT);

  return {
    balance,
    pnl,
    startingBalance: starting,
    peakBalance: roundToCent(peakBalance),
    lowestBalance: roundToCent(lowestBalance),
    currentDrawdown: roundToCent(peakBalance - balance),
    maxDrawdown: roundToCent(maxDrawdown),
    maxDeficit: roundToCent(Math.max(0, -lowestBalance)),
    series: downsampleBalanceSeries(series),
    markers,
    recentEvents,
    strategies: strategyStats,
  };
}

function eventRank(kind: MonitorEvent['kind']): number {
  switch (kind) {
    case 'STOP':
      return 0;
    case 'CONFIG':
      return 1;
    case 'BELOW_ZERO':
    case 'RECOVERED':
      return 2;
    case 'NEW_LOW':
    case 'NEW_PEAK':
      return 3;
    default:
      return 4;
  }
}
